import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Icon from './Icon';
import { colors } from '../styles';
import { Spinner } from "modules/theme";

const types = {
  default: {
    background: "#4860D9",
    color: "#FDFFFC",
  },
  primary: {
    background: colors.colorSecondary
  },
  success: {
    background: colors.colorCoreGreen
  },
  danger: {
    background: colors.colorCoreRed
  },
  warning: {
    background: colors.colorCoreYellow
  },
  simple: {
    background: colors.colorWhite,
    borderColor: colors.themeBlue,
    color: colors.themeBlack
  },
  link: {
    background: 'transparent',
    color: '#B1B1B1'
  },
  secondary: {
    background: colors.colorCoreLGray,
    color: colors.colorCoreGray
  },
  cancel: {
    background: colors.colorLGray,
    color: "#B1B1B1"
  },
  filter: {
    background: '#DDE9F9',
    color: colors.themeBlue
  }
};

const sizes = {
  large: {
    padding: '10px 30px',
    fontSize: '13px'
  },
  medium: {
    padding: '6px 22px',
    fontSize: '12px',
    borderRadius: '8px'
  },
  small: {  
    fontSize: '12px',
    borderRadius: '8px',
    padding: '9px 12px',
    fontWeight: '500'
  },
  xl: {
    borderRadius: '15px',
    fontSize: '15px',
    padding: '12px 35px',
    fontWeight: '500'
  }
};

const ButtonStyled = styled.button`
  border-radius: 25px;
  position: relative;
  transition: all 0.3s ease;
  text-transform: uppercase;
  outline: 0;
  line-height: 12px;

  ${props => css`
    padding: 12px 20px;
    background: ${
      types[props.btnStyle].background
        ? types[props.btnStyle].background
        : colors.colorPrimary
    };
    font-size: ${sizes[props.size].fontSize};
    color: ${
      types[props.btnStyle].color
        ? types[props.btnStyle].color
        : colors.colorWhite
    };
    border: ${
      props.btnStyle === 'simple' ? `1px solid ${colors.colorPrimary}` : 'none'
    };
    display: ${props.block && 'block'};
    width: ${props.block && '100%'};
    height: ${sizes[props.size].height};
    border-radius: ${sizes[props.size].borderRadius};
    font-weight: ${sizes[props.size].fontWeight};

    &:hover {
      cursor: pointer;
      text-decoration: none;
      color: ${
        props.hoverColor? props.hoverColor:
        types[props.btnStyle].color
          ? types[props.btnStyle].color : colors.colorWhite
      };
    }

    &:disabled {
      cursor: not-allowed !important;
    }
  `};

  a {
    color: ${colors.colorWhite};
  }

  > i + span,
  > span + i,
  > span i {
    @media(max-width: 760px)
    {
      display: none;
    }
    margin-left: 5px;
  }
`;

const ButtonLink = ButtonStyled.withComponent('a').extend`
  text-decoration: inherit;
  text-align: center;

  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      background: lighten(types[props.btnStyle].background, 30);
    `};
`;

const ButtonGroup = styled.div`
  position: relative;
  display: inline-block;
  vertical-align: middle;

  button + a,
  a + button {
    margin-left: 10px;
  }
`;

function Button({ ...props }, { __ }) {
  const Element = props.href || props.type === 'link' ? ButtonLink : ButtonStyled;

  let content = props.children;

  if (!props.ignoreTrans && typeof content === 'string' && __) {
    content = __(content);
  }

  if (props.icon) {
    return (
      <Element {...props}>
        <Icon icon={props.icon} />
        {content && <span>{content}</span>}
      </Element>
    );
  }

  return (
    <Element {...props} style={props.style}>
      {props.isLoading === true ? (
        <Spinner
          style={{
            height: "3px",
            marginTop: "-15px",
            padding: "10px",
            marginBottom: "10px",
          }}
        />
      ) : (
        content
      )}
    </Element>
  );
}

function Group({ children }) {
  return <ButtonGroup>{children}</ButtonGroup>;
}

Button.Group = Group;

Group.propTypes = {
  children: PropTypes.node
};

Button.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func,
  href: PropTypes.string,
  type: PropTypes.string,
  btnStyle: PropTypes.oneOf([
    'default',
    'primary',
    'success',
    'danger',
    'warning',
    'simple',
    'link',
    'secondary'
  ]),
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  disabled: PropTypes.bool,
  ignoreTrans: PropTypes.bool,
  block: PropTypes.bool,
  icon: PropTypes.string
};

Button.contextTypes = {
  __: PropTypes.func
};

Button.defaultProps = {
  btnStyle: 'default',
  size: 'medium',
  block: false,
  type: 'button'
};

export default Button;



// WEBPACK FOOTER //
// src/modules/theme/theme1/common/components/Button.js